import type { ConversationMessageApiModel as ChatMessage, UsedSource } from "@noa/api-core";

interface ParseMessageWithReferencesResult {
  content: string;
  references: Map<number, string[]>;
}

export interface ReferenceMapValue {
  refNumber: number;
  used_sources?: UsedSource;
}

const SOURCE_PATTERN = /<!--SOURCES:\s*([^>]+)-->/g;

const parseMatchedRefIDs = (match: RegExpMatchArray): string[] => {
  if (!match[1]) return [];

  return match[1]
    .split(",")
    .map((refID) => refID.trim())
    .filter((refID) => refID.length > 0);
};

interface ProcessSourceMatchesArgs {
  matches: RegExpMatchArray[];
  content: string;
}

const processSourceMatches = ({ matches, content }: ProcessSourceMatchesArgs) => {
  const references = new Map<number, string[]>();

  let processedContent = content;
  let referenceNumber = 1;

  for (const match of matches) {
    if (!match[1]) continue;

    const refIDs = parseMatchedRefIDs(match);

    if (refIDs.length > 0) {
      references.set(referenceNumber, refIDs);
      const referenceMark = ` *[${referenceNumber}]*`;
      processedContent = processedContent.replace(match[0], referenceMark);
      referenceNumber++;
    } else {
      processedContent = processedContent.replace(match[0], "");
    }
  }

  return { references, processedContent };
};

export const parseMessageWithReferences = (message: string): ParseMessageWithReferencesResult => {
  const hasIncompleteSource = message.includes("<!--SOURCES:") && !message.includes("-->");
  if (hasIncompleteSource) {
    const content = message.split("<!--SOURCES:")[0].trim();
    return { content, references: new Map() };
  }

  const matches = Array.from(message.matchAll(SOURCE_PATTERN));
  if (matches.length === 0) return { content: message, references: new Map() };

  const { references, processedContent } = processSourceMatches({ matches, content: message });

  // replace \n*[number]* with *[$number]*
  const content = processedContent.trim().replace(/\n\s*\*\[(\d+)\]\*/g, " *[$1]*");

  return { content, references };
};

interface CreateReferenceMapArgs {
  references: Map<number, string[]>;
  chatMessage: ChatMessage;
}

export const createReferenceMap = ({ references, chatMessage }: CreateReferenceMapArgs) => {
  const refMap = new Map<string, ReferenceMapValue>();

  Array.from(references.entries()).forEach(([refNumber, refIds]) => {
    refIds.forEach((refId) => {
      const used_sources = chatMessage.used_sources.find((source) => source.unique === refId);
      refMap.set(refId, { refNumber, used_sources });
    });
  });

  return refMap;
};

export const normalizeTabsAndLineBreaks = (text?: string | null): string => {
  if (!text) return "";

  return text.replace(/\\n/g, "\n").replace(/\\t/g, "\t");
};
